import { create } from 'zustand';
import type { Client } from '@microsoft/microsoft-graph-client';
import { listFolder, readFileContent } from '@/lib/graph/onedrive-helpers';
import type { WeeklySummary, WeeklyProposal } from '@/types/output';
import { parseWeeklySummary } from '@/types/output';
import type { DriveItem } from '@/lib/graph/client';

const WEEKLY_PATH = 'weekly';

// Cache duration: 5 minutes
const CACHE_DURATION_MS = 5 * 60 * 1000;

// Logger - enable for debugging
const DEBUG_OUTPUTS = false;
const log = (message: string, data?: unknown) => {
  if (!DEBUG_OUTPUTS) return;
  const timestamp = new Date().toISOString().split('T')[1];
  console.log(`[${timestamp}] 📄 Outputs: ${message}`, data ?? '');
};

interface FileWithPath {
  file: DriveItem;
  path: string;
}

/**
 * Convert various response types to string
 */
async function ensureString(response: unknown): Promise<string> {
  if (typeof response === 'string') {
    return response;
  }
  
  if (response instanceof Blob) {
    return await response.text();
  }
  
  if (response instanceof ArrayBuffer) {
    return new TextDecoder().decode(response);
  }
  
  if (response && typeof response === 'object') {
    return JSON.stringify(response);
  }
  
  return String(response ?? '');
}

interface OutputsState {
  outputs: WeeklySummary[];
  pendingProposal: WeeklyProposal | null;
  selectedOutput: WeeklySummary | null;
  isLoading: boolean;
  error: string | null;
  lastLoadedAt: number | null;

  // Actions
  loadOutputs: (graph: Client, forceRefresh?: boolean) => Promise<void>;
  loadOutputContent: (graph: Client, output: WeeklySummary) => Promise<string>;
  selectOutput: (output: WeeklySummary | null) => void;
  loadPendingProposal: (graph: Client, forceRefresh?: boolean) => Promise<void>;
  invalidateCache: () => void;
}

export const useOutputsStore = create<OutputsState>((set, get) => ({
  outputs: [],
  pendingProposal: null,
  selectedOutput: null,
  isLoading: false,
  error: null,
  lastLoadedAt: null,

  loadOutputs: async (graph: Client, forceRefresh = false) => {
    const { outputs, lastLoadedAt, isLoading } = get();
    
    // Skip if already loading
    if (isLoading) return;
    
    // Check cache validity
    const now = Date.now();
    const isCacheValid = outputs.length >= 0 && lastLoadedAt && (now - lastLoadedAt < CACHE_DURATION_MS);
    
    if (isCacheValid && !forceRefresh) {
      log('Using cached outputs data');
      return;
    }
    
    log('Loading outputs from weekly folder...');
    set({ isLoading: true, error: null });

    try {
      const items = await listFolder(graph, WEEKLY_PATH);
      log(`Found ${items.length} items in weekly folder`);
      
      // Collect all markdown files - both direct and from subfolders
      const allMdFiles: FileWithPath[] = [];
      
      for (const item of items) {
        if (item.name.endsWith('.md')) {
          allMdFiles.push({ file: item, path: `${WEEKLY_PATH}/${item.name}` });
        } else if (item.folder) {
          try {
            const subItems = await listFolder(graph, `${WEEKLY_PATH}/${item.name}`);
            for (const subItem of subItems) {
              if (subItem.name.endsWith('.md')) {
                allMdFiles.push({ 
                  file: subItem, 
                  path: `${WEEKLY_PATH}/${item.name}/${subItem.name}` 
                });
              }
            }
          } catch {
            // Skip folders we can't read
          }
        }
      }
      
      // Sort by date (most recent first)
      allMdFiles.sort((a, b) => 
        new Date(b.file.lastModifiedDateTime).getTime() - new Date(a.file.lastModifiedDateTime).getTime()
      );

      log(`Processing ${allMdFiles.length} markdown files`);

      // Load content for each file
      const loadedOutputs: WeeklySummary[] = [];
      
      for (const { file, path } of allMdFiles) {
        try {
          const rawContent = await readFileContent(graph, path);
          const content = await ensureString(rawContent);
          const weekMatch = file.name.match(/(\d{4})-W(\d{2})/);
          
          loadedOutputs.push({
            id: file.id,
            week: weekMatch ? parseInt(weekMatch[2], 10) : 0,
            year: weekMatch ? parseInt(weekMatch[1], 10) : new Date().getFullYear(),
            filePath: path,
            webUrl: file.webUrl,
            content,
            sections: parseWeeklySummary(content),
            createdUtc: file.createdDateTime,
            updatedUtc: file.lastModifiedDateTime,
          });
        } catch (err) {
          log(`Error loading file ${path}:`, err);
        }
      }

      log(`Loaded ${loadedOutputs.length} outputs successfully`);
      set({ outputs: loadedOutputs, isLoading: false, lastLoadedAt: Date.now() });
    } catch (error) {
      log('Failed to load outputs:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load outputs',
        isLoading: false,
      });
    }
  },

  loadOutputContent: async (graph: Client, output: WeeklySummary) => {
    const rawContent = await readFileContent(graph, output.filePath);
    return await ensureString(rawContent);
  },

  selectOutput: (output: WeeklySummary | null) => {
    set({ selectedOutput: output });
  },

  loadPendingProposal: async (graph: Client, forceRefresh = false) => {
    const { pendingProposal, lastLoadedAt } = get();
    
    // Use cached proposal if available and cache is valid
    const now = Date.now();
    if (!forceRefresh && pendingProposal !== null && lastLoadedAt && (now - lastLoadedAt < CACHE_DURATION_MS)) {
      return;
    }
    
    log('Loading pending proposals...');
    try {
      const stateFiles = await listFolder(graph, 'state');
      
      for (const file of stateFiles) {
        if (file.name.endsWith('.json')) {
          const rawContent = await readFileContent(graph, `state/${file.name}`);
          const content = await ensureString(rawContent);
          if (content) {
            try {
              const state = JSON.parse(content);
              if (state.weeklyProposal?.proposalData) {
                log('Found pending proposal', file.name);
                set({ pendingProposal: state.weeklyProposal });
                return;
              }
            } catch {
              // Skip invalid JSON
            }
          }
        }
      }
      
      set({ pendingProposal: null });
    } catch (error) {
      log('Failed to load pending proposal', error);
      set({ pendingProposal: null });
    }
  },

  invalidateCache: () => {
    set({ lastLoadedAt: null });
  },
}));
